/**
 * Dev-login page (TEST/DEV ONLY) — reachable at /dev-login.
 *
 * Lets the holder of DEV_LOGIN_SECRET log in as any role without Entra/MSAL.
 * Not linked from the nav or global search; you reach it by typing the URL.
 * The backend refuses to mint a token on production, and getDevSession() is
 * host-gated, so this page is inert on intern.fullframe.nl.
 */
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Paper, Typography, TextField, Button, MenuItem, Alert, Chip,
} from '@mui/material';
import { BACKEND_URL } from '../../utils/apiConfig';
import { leesJson } from '../../utils/apiFout';
import { setDevSession, isDevLoginHost } from '../../utils/devSession';

export default function DevLogin() {
  const navigate = useNavigate();
  const [status, setStatus] = useState({ enabled: null, roles: [], environment: '' });
  const [email, setEmail] = useState('dev@fullframe.nl');
  const [role, setRole] = useState('admin');
  const [secret, setSecret] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!isDevLoginHost) { setStatus({ enabled: false, roles: [], environment: 'production' }); return; }
    fetch(`${BACKEND_URL}/Api/dev-login/status`)
      .then((r) => leesJson(r, 'Dev-login-status ophalen'))
      .then((d) => {
        setStatus(d);
        if (d.roles?.length && !d.roles.includes(role)) setRole(d.roles.includes('admin') ? 'admin' : d.roles[0]);
      })
      .catch((e) => {
        setStatus({ enabled: false, roles: [], environment: 'onbekend' });
        setError(e.message);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    setBusy(true);
    try {
      const res = await fetch(`${BACKEND_URL}/Api/dev-login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ secret, email, role }),
      });
      const data = await leesJson(res, 'Inloggen'); // leest data.error uit de body; ook leesbaar bij HTML/500
      setDevSession({ token: data.token, email: data.email, role: data.role });
      navigate('/', { replace: true });
      // Full reload so MsalProvider-derived state and the role context re-init cleanly.
      window.location.reload();
    } catch (err) {
      setError(err.message);
      setBusy(false);
    }
  };

  const disabled = status.enabled === false;

  return (
    <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: '#f8f9fa' }}>
      <Paper elevation={0} sx={{ maxWidth: 420, width: '100%', p: 5, mx: 2, borderRadius: 3, border: '1px solid rgba(33,45,56,0.08)', boxShadow: '0 4px 24px -4px rgba(33,45,56,0.06)' }}>
        <Box sx={{ textAlign: 'center', mb: 3 }}>
          <Typography variant="h5" fontWeight={800} sx={{ letterSpacing: '-0.02em', mb: 0.5 }}>
            Toolframe
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Dev-login: snel inloggen als elke rol — alleen test/lokaal
          </Typography>
          {status.environment && (
            <Chip size="small" label={`omgeving: ${status.environment}`} sx={{ mt: 1.5 }} color={disabled ? 'default' : 'success'} variant="outlined" />
          )}
        </Box>

        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        {disabled ? (
          <Alert severity="error">
            Dev-login staat hier uit. Dit werkt alleen op test/lokaal met een ingesteld
            <code> DEV_LOGIN_SECRET</code> — nooit op productie.
          </Alert>
        ) : (
          <form onSubmit={submit}>
            <TextField
              label="E-mail (wie je wilt zijn)" fullWidth size="small" sx={{ mb: 2 }}
              value={email} onChange={(e) => setEmail(e.target.value)} autoComplete="off"
            />
            <TextField
              label="Rol" select fullWidth size="small" sx={{ mb: 2 }}
              value={role} onChange={(e) => setRole(e.target.value)}
            >
              {(status.roles || ['admin']).map((r) => (
                <MenuItem key={r} value={r}>{r}</MenuItem>
              ))}
            </TextField>
            <TextField
              label="Secret" type="password" fullWidth size="small" sx={{ mb: 3 }}
              value={secret} onChange={(e) => setSecret(e.target.value)}
              autoFocus autoComplete="off"
            />
            <Button
              type="submit" variant="contained" fullWidth disabled={busy || !secret}
              sx={{ py: 1.3, fontWeight: 600, textTransform: 'none', borderRadius: 2, bgcolor: '#526075', '&:hover': { bgcolor: '#237a77' } }}
            >
              {busy ? 'Bezig…' : 'Inloggen'}
            </Button>
          </form>
        )}
      </Paper>
    </Box>
  );
}
